import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { Box, Typography, Button } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { isEmptyObject, hasNoQuotes } from '../../utils/apiHelpers';
import { useLocation } from 'react-router-dom';
import SeoTagsManager from '../../utils/SeoTagsManager';

import MobileProviderTileComponent from '../../components/ProviderTile/MobileProviderTileComponent_MedicalAid';
import ProviderTileComponent from '../../components/ProviderTile/ProviderTileComponent_MedicalAid';

import FilterPanelComponent from '../../components/FilterPanel/FilterPanelComponent_Dynamic';
import MobileFilterPanelComponent from '../../components/FilterPanel/MobileFilterPanelComponent_Dynamic';
import PopupModalComponent from '../../components/PopupModal/PopupModalComponent';
import ResultsHeader from '../../components/ResultHeader/ResultHeaderComponent';
import MobileResultsHeader from '../../components/ResultHeader/MobileResultHeaderComponent';
import { useMobileContext } from '../../contexts/MobileContext';

import {
  getMedicalAidQuote,
  getMedicalAidQuoteResultsToggle,
} from '../../api/apiServices';
import { withRouter } from '../../utils/withRouter';
import TakeMeHomeButton from '../../components/TakeMeHomeButton/TakeMeHomeButton';
import {
  getCrossSellQuotesForResults,
  getCrossSellQuoteDetails,
} from '../../utils/crossSellResultsHelper';
import {
  markProductChosen,
  unmarkProductChosen,
  isProductChosen,
  getLandingPathForFallbackKey,
} from '../../utils/carouselQuoteHelpers';
import { pushQuoteResultsView } from '../../utils/dataLayer';

import FilterPanelData from '../../json/components/FilterPanel/FilterPanel_MedicalAid.json';

import gsap from 'gsap';

// Fireworks (match Quote page)
import lottie from 'lottie-web';
import fireworksData from '../../assets/lottie/fireworks.json';

const FilterIcon = 'https://assets.askhippo.co.za/Icons/common/filter.svg';
/* ---------------------------------------
   Fireworks overlay
----------------------------------------*/
const FireworksFromTop = ({ playSignal, isMobile }) => {
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const leftLottieRef = useRef(null);
  const rightLottieRef = useRef(null);

  useEffect(() => {
    if (leftRef.current) {
      leftLottieRef.current = lottie.loadAnimation({
        container: leftRef.current,
        animationData: fireworksData,
        renderer: 'svg',
        loop: false,
        autoplay: false,
      });
    }
    if (rightRef.current) {
      rightLottieRef.current = lottie.loadAnimation({
        container: rightRef.current,
        animationData: fireworksData,
        renderer: 'svg',
        loop: false,
        autoplay: false,
      });
    }
    return () => {
      leftLottieRef.current?.destroy();
      rightLottieRef.current?.destroy();
      leftLottieRef.current = null;
      rightLottieRef.current = null;
    };
  }, []);

  useEffect(() => {
    const playForDurationMs = (instance, desiredMs = 2500) => {
      if (!instance) return;
      const nativeSecs =
        typeof instance.getDuration === 'function'
          ? instance.getDuration(false)
          : null;
      const speed =
        nativeSecs && nativeSecs > 0 ? nativeSecs / (desiredMs / 1000) : 1;
      instance.setSpeed(speed);
      instance.goToAndPlay(0);
    };
    playForDurationMs(leftLottieRef.current, 3500);
    playForDurationMs(rightLottieRef.current, 3500);
  }, [playSignal]);

  function getDeviceScreenSize() {
    if (typeof window === 'undefined') {
      return { width: 0, height: 0 };
    }
    return {
      width: window.screen.width,
      height: window.screen.height,
    };
  }

  const { width } = getDeviceScreenSize();
  const FW_SIZE = isMobile ? Math.max(320, Math.round(width * 0.7)) : 520;
  const HALF = FW_SIZE / 2;

  return (
    <>
      <Box
        ref={leftRef}
        sx={{
          position: 'absolute',
          top: `-${HALF}px`,
          left: 0,
          width: `${FW_SIZE}px`,
          height: `${FW_SIZE}px`,
          transform: 'translateX(-35%) rotate(-35deg)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <Box
        ref={rightRef}
        sx={{
          position: 'absolute',
          top: `-${HALF}px`,
          right: 0,
          width: `${FW_SIZE}px`,
          height: `${FW_SIZE}px`,
          transform: 'translateX(35%) rotate(35deg)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
    </>
  );
};

function computeInitialSelectedSections(formValues, panelData) {
  const ss = {};

  // 1) Start with each section’s own default
  Object.entries(panelData).forEach(([key, section]) => {
    if (!section || typeof section !== 'object') return;

    if (section.type === 'radio') {
      ss[key] =
        section.defaultSelectedOption ?? section.values?.[0]?.value ?? '';
    } else if (section.type === 'checkbox') {
      ss[key] = Array.isArray(section.defaultSelectedOptions)
        ? section.defaultSelectedOptions
        : [];
    } else if (section.type === 'slider') {
      // Panel treats selectedSections.coverValue as a single number
      // and will clamp it after first API fetch when bounds are known.
      const v = Number.isFinite(section.defaultValue)
        ? section.defaultValue
        : 0;
      ss[key] = v;
    }
  });

  // 2) Your special mapping rules
  // hospitalChoice: If formValues.hospitalChoice === 'Network Hospital' -> 'Network' else 'Any Private'
  if (formValues?.hospitalChoice) {
    ss.hospitalChoice =
      formValues.hospitalChoice === 'Network Hospital'
        ? 'Network'
        : 'Any Private';
  }

  // treatmentExclusion: based on treatmentsCovered. If "More Exclusions" -> "Yes" else "No"
  if (formValues?.treatmentsCovered) {
    ss.treatmentExclusion =
      formValues.treatmentsCovered === 'More Exclusions' ? 'Yes' : 'No';
  }

  // levelDayToDayCover: map from Question 5's dayTreatmentsCovered
  if (formValues?.dayTreatmentsCovered) {
    const dayMap = {
      'Comprehensive day-to-day cover': 'Full Cover',
      'Some day-to-day cover': 'Some Treatment',
      'Hospital cover only': 'Hospital Only',
    };
    ss.levelDayToDayCover =
      dayMap[formValues.dayTreatmentsCovered] ?? ss.levelDayToDayCover;
  }

  return ss;
}

function ResultPage(props) {
  const { navigate, isMobile, isTablet } = props;
  const INITIAL_TILE_APPEAR_MS = 300;
  const REVEAL_STAGGER_MS = 500;
  const BASE_REVEAL_DELAY_MS = 800;

  const { category, product, formValues } = props.location?.state || {};
  const isMobileOrTablet = isMobile || isTablet;
  const bestDealRef = useRef(null);
  const introPanelRef = useRef(null);

  const crossSellQuoteDetails = getCrossSellQuoteDetails(props.location?.state);
  const medicalAidAdults =
    formValues?.adultsCoveredMedicalAid ??
    crossSellQuoteDetails?.['Adults'] ??
    0;
  const medicalAidChildren =
    formValues?.childrenCoveredMedicalAid ??
    crossSellQuoteDetails?.['Children'] ??
    0;

  // States
  const [loading, setLoading] = useState(
    !sessionStorage.getItem('hippo_reveal_done')
  );
  const [fetching, setFetching] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [bestDealContentReady, setBestDealContentReady] = useState(
    !!sessionStorage.getItem('hippo_reveal_done')
  );
  const [showTeaseModal, setShowTeaseModal] = useState(false);
  const [allowFinalTile, setAllowFinalTile] = useState(false);
  const [hideTop3Permanently, setHideTop3Permanently] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [headerText, setHeaderText] = useState('');
  const [showIntroPanel, setShowIntroPanel] = useState(
    !props.location?.state?.fromCrossSell
  );
  const [introDelayDone, setIntroDelayDone] = useState(
    !!props.location?.state?.fromCrossSell
  );
  const [quotesForAnimation, setQuotesForAnimation] = useState([]);
  const [avgPremium, setAvgPremium] = useState(0);
  const [visibleQuotes, setVisibleQuotes] = useState(0);
  const scaledOnceRef = useRef(new Set());

  // Fireworks control
  const [fireworksSignal, setFireworksSignal] = useState(0);
  const [fireworksArmed, setFireworksArmed] = useState(false);
  const hasSeenRevealRef = useRef(
    sessionStorage.getItem('hippo_reveal_done') === '1'
  );

  // Filter panel config (persist across fetches)
  const [filterConfig, setFilterConfig] = useState(() => {
    const selectedSections = computeInitialSelectedSections(
      formValues || {},
      FilterPanelData
    );

    return {
      ...FilterPanelData,
      selectedSections,
    };
  });
  const [appliedSchemes, setAppliedSchemes] = useState(null); // null = all; populated after first quote load
  // Tracks non-dynamicValues selections last sent to the API.
  // Initialised from panel defaults so a dynamicValues-only first Apply skips the fetch.
  const lastAppliedApiFiltersRef = useRef(
    JSON.stringify(
      Object.fromEntries(
        Object.entries(
          computeInitialSelectedSections(formValues || {}, FilterPanelData)
        ).filter(([k]) => !FilterPanelData[k]?.dynamicValues)
      )
    )
  );

  // Live form payload for API
  const [liveFormValues, setLiveFormValues] = useState(() => ({
    ...(formValues || {}),
    // resultsPageToggle: true,
  }));

  useEffect(() => {
    if (isProductChosen('latestMedicalAidQuotes')) {
      navigate(getLandingPathForFallbackKey('latestMedicalAidQuotes'), {
        replace: true,
      });
      return;
    }
    fetchQuotes(liveFormValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Animation phases
  useEffect(() => {
    if (
      fetching ||
      initialLoadDone ||
      !introDelayDone ||
      quotes.length === 0 ||
      quotesForAnimation.length > 0
    ) {
      return;
    }

    const nonTop3 = quotes.filter((q) => !q.isTop3);
    if (nonTop3.length > 0) {
      const animationOrder = [...nonTop3].sort(
        (a, b) =>
          b.premium.monthlypremium_total - a.premium.monthlypremium_total
      );

      let delay = 0;
      const step = INITIAL_TILE_APPEAR_MS;
      const firstGap = Math.round(step * 3);

      animationOrder.forEach((quote, index) => {
        setTimeout(() => {
          setQuotesForAnimation((prev) => [quote, ...prev]);
          if (index === animationOrder.length - 1) {
            if (quotes.length > 5) {
              if (hasSeenRevealRef.current) {
                setTimeout(() => setAllowFinalTile(true), 600);
              } else {
                sessionStorage.setItem('hippo_reveal_done', '1');
                setTimeout(() => setShowTeaseModal(true), 1000);
              }
            } else {
              setTimeout(() => setAllowFinalTile(true), 600);
            }
          }
        }, delay);
        delay += index === 0 ? firstGap : step;
      });
    } else {
      setTimeout(() => setAllowFinalTile(true), 400);
    }
  }, [fetching, initialLoadDone, quotes, quotesForAnimation, introDelayDone]);

  useEffect(() => {
    if (!allowFinalTile || quotes.length === 0) return;

    const top3Ascending = [...quotes]
      .filter((q) => q.isTop3)
      .sort(
        (a, b) =>
          a.premium.monthlypremium_total - b.premium.monthlypremium_total
      );

    const revealOrder = [
      top3Ascending[2],
      top3Ascending[1],
      top3Ascending[0],
    ].filter(Boolean);

    if (revealOrder.length === 0) {
      setInitialLoadDone(true);
      return;
    }

    revealOrder.forEach((q, idx) => {
      setTimeout(
        () => {
          setQuotesForAnimation((prev) => [q, ...prev]);
          if (q?.bestDeal && bestDealRef.current && !initialLoadDone) {
            setTimeout(() => setInitialLoadDone(true), 300);
          }
        },
        BASE_REVEAL_DELAY_MS + idx * REVEAL_STAGGER_MS
      );
    });
  }, [allowFinalTile, quotes, initialLoadDone]);

  useEffect(() => {
    if (!fetching && !showIntroPanel) {
      setTimeout(() => setIntroDelayDone(true), 2000);
    }
  }, [fetching, showIntroPanel]);

  const ProviderTile = isMobileOrTablet
    ? MobileProviderTileComponent
    : ProviderTileComponent;

  const ResultHeaderComponent = isMobileOrTablet
    ? MobileResultsHeader
    : ResultsHeader;

  useEffect(() => {
    if (hasSeenRevealRef.current) return;
    const timer = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!loading && bestDealRef.current && allowFinalTile && !initialLoadDone) {
      setTimeout(() => setInitialLoadDone(true), 1200);
    }
  }, [loading, allowFinalTile, initialLoadDone]);

  /* ------------------------------
     GSAP helpers
  -------------------------------*/
  const timelinesRef = useRef(new Map());

  const playEntrance = (el, { isBestDeal }) => {
    if (!el) return;
    const motionDiv = el.closest('[data-tile-motion]');

    const prev = timelinesRef.current.get(el.id);
    if (prev) prev.kill();

    const bounceSeq = (target) =>
      gsap
        .timeline()
        .set(
          target,
          { transformOrigin: '50% 50%', rotateZ: 0, scale: 0.75, opacity: 1 },
          0
        )
        .to(target, { scale: 1.06, duration: 0.4, ease: 'power3.out' }, 0)
        .to(
          target,
          { scale: 1.0, duration: 1.0, ease: 'elastic.out(1, 0.75)' },
          '>-0.02'
        );

    const tiltSeq = (target) =>
      gsap
        .timeline({ defaults: { ease: 'power2.inOut' } })
        .to(
          target,
          { duration: 1.2, rotateZ: -4, scale: 1.15, ease: 'power1.out' },
          0
        )
        .to(
          target,
          {
            duration: 1.5,
            rotateZ: 0,
            scale: 1.0,
            ease: 'elastic.out(1.5, 0.4)',
          },
          '>-0.02'
        );

    const tl = gsap.timeline();
    tl.add(bounceSeq(el), 0);
    if (isBestDeal && !hasSeenRevealRef.current) tl.add(tiltSeq(el), '>-0.90');

    if (isBestDeal) {
      tl.add(
        () => {
          const shouldReveal = !hasSeenRevealRef.current;
          if (shouldReveal) {
            setFireworksArmed(true);
            hasSeenRevealRef.current = true;
            sessionStorage.setItem('hippo_reveal_done', '1');
          }
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setBestDealContentReady(true);
              if (shouldReveal) setFireworksSignal((s) => s + 1);
              setInitialLoadDone(true);
            });
          });
        },
        hasSeenRevealRef.current ? '<' : '>-1.7'
      );
    }

    tl.add(() => {
      if (motionDiv) gsap.set(motionDiv, { zIndex: 'auto' });
    });

    timelinesRef.current.set(el.id, tl);
    return tl;
  };

  const handleSelectQuote = useCallback(
    (e, { data }) => {
      const hasGap = (data.premium?.monthlypremium_gap ?? 0) > 0;
      let datatoSend = {
        QuotePK: data.QuotePK,
        FriendlyName: data.schemename,
        LogoURL: data.schemeinfo.logo.normal,
        TotalPremium: hasGap
          ? data.premium.monthlypremium_medicalaid
          : data.premium.monthlypremium_total,
      };
      markProductChosen('latestMedicalAidQuotes');
      navigate('/quoteSelection', {
        replace: true,
        state: { ...datatoSend, _product: product },
      });
    },
    [navigate, product]
  );

  const handleBuyOnline = useCallback(
    (e, { data }) => {
      markProductChosen('latestMedicalAidQuotes');
      navigate('/quoteSelection', {
        replace: true,
        state: { ...data, _product: product },
      });
    },
    [navigate, product]
  );

  const { pathname } = useLocation();

  /* ------------------------------
     API + panel sync
  -------------------------------*/
  const fetchQuotes = useCallback(async (values) => {
    const crossSellQuotes = getCrossSellQuotesForResults(props.location?.state);
    if (!crossSellQuotes && isEmptyObject(values)) {
      setFetching(false);
      return;
    }

    setFetching(true);

    let quotesFromAPI;
    if (crossSellQuotes) {
      quotesFromAPI = crossSellQuotes;
    } else {
      const quotesFromAPICombined = values.resultsPageToggle
        ? await getMedicalAidQuoteResultsToggle(values).catch(() => [])
        : await getMedicalAidQuote(values).catch(() => []);
      quotesFromAPI = quotesFromAPICombined[0]?.Quotes || [];
      quotesFromAPI.forEach((quote) => {
        quote.QuotePK = quote.QuotePk;
      });
    }

    if (hasNoQuotes(quotesFromAPI)) {
      setInitialLoadDone(true);
      setFetching(false);
      setQuotes([]);
      setQuotesForAnimation([]);
      return;
    }

    const avgPremiumValue =
      quotesFromAPI.reduce(
        (sum, q) => sum + q.premium.monthlypremium_total,
        0
      ) / quotesFromAPI.length;
    setAvgPremium(avgPremiumValue);

    const ranked = [...quotesFromAPI]
      .sort(
        (a, b) =>
          a.premium.monthlypremium_total - b.premium.monthlypremium_total
      )
      .map((q, i) => ({ ...q, _rank: i + 1 }));

    const top3Ids = new Set(
      ranked.slice(0, Math.min(3, ranked.length)).map((q) => q.optionid)
    );
    const bestId = ranked[0]?.optionid;

    const updatedQuotes = ranked.map((quote) => {
      const tags = [];
      const isBestDeal = quote.optionid === bestId;
      const isTop3 = top3Ids.has(quote.optionid);

      const onlineDiscount = quote.OnlinePremium
        ? Math.round(
            ((quote.premium.monthlypremium_total - quote.OnlinePremium) /
              quote.premium.monthlypremium_total) *
              100
          )
        : 0;

      if (isBestDeal) {
        const improvement = Math.round(
          ((avgPremiumValue - quote.premium.monthlypremium_total) /
            avgPremiumValue) *
            100
        );
        tags.push({
          text: `${improvement}% Better than the avg.`,
          color: '#67F4F0',
        });
      } else if (onlineDiscount > 0) {
        tags.push({
          text: `${onlineDiscount}% Discount when you Buy Online!`,
          color: '#E3E800',
        });
      }

      return { ...quote, bestDeal: isBestDeal, isTop3, RetailChips: tags };
    });

    setQuotes(updatedQuotes);
    pushQuoteResultsView({ product });
    localStorage.setItem(
      'latestMedicalAidQuotes',
      JSON.stringify(updatedQuotes)
    );
    unmarkProductChosen('latestMedicalAidQuotes');

    // Seed dynamic checkbox values from quotes
    const dynamicValueMap = {};
    Object.entries(FilterPanelData).forEach(([pathKey, def]) => {
      if (def?.type !== 'checkbox' || !def.dynamicValues) return;
      const quotePath = def.valuesPath ?? pathKey;
      const seen = new Set();
      const values = [];
      for (const q of quotesFromAPI) {
        const raw = q[quotePath];
        const items = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
        for (const item of items) {
          const str = String(item);
          if (!seen.has(str)) {
            seen.add(str);
            values.push({ label: def.labelMap?.[str] ?? str, value: str });
          }
        }
      }
      if (values.length) dynamicValueMap[pathKey] = values;
    });

    setFilterConfig((prev) => {
      const prevSelected = prev?.selectedSections || {};
      const configUpdates = {};
      const selectedUpdates = {};
      for (const [pathKey, values] of Object.entries(dynamicValueMap)) {
        configUpdates[pathKey] = { ...FilterPanelData[pathKey], values };
        selectedUpdates[pathKey] = values.map((v) => v.value); // all selected = inclusive
      }
      return {
        ...prev,
        ...configUpdates,
        selectedSections: { ...prevSelected, ...selectedUpdates },
      };
    });

    const schemeValues =
      dynamicValueMap.schemename?.map((v) => v.value) ?? null;
    if (schemeValues) setAppliedSchemes(schemeValues);

    const lowestPremium = Math.min(
      ...quotesFromAPI.map((q) => q.premium.monthlypremium_total)
    );
    const improvement = Math.round(avgPremiumValue - lowestPremium);
    setHeaderText(
      `Your best price is <b>R${improvement} better</b> than average.`
    );

    setFetching(false);
  }, []);

  const handleApply = async (cfgFromPanel) => {
    setFilterConfig(cfgFromPanel);

    const ss = cfgFromPanel?.selectedSections || {};

    // dynamicValues sections are local-only; everything else goes to the API
    const isDynamic = (k) => !!FilterPanelData[k]?.dynamicValues;

    setAppliedSchemes(ss.schemename ?? null);

    const apiFilters = Object.fromEntries(
      Object.entries(ss).filter(([k]) => !isDynamic(k))
    );
    const apiFiltersJson = JSON.stringify(apiFilters);

    if (apiFiltersJson === lastAppliedApiFiltersRef.current) {
      setShowMobileFilterPanel(false);
      return;
    }
    lastAppliedApiFiltersRef.current = apiFiltersJson;

    const next = {
      ...liveFormValues,
      resultsPageToggle: true,
      filters: apiFilters,
    };

    setLiveFormValues(next);

    // Reset reveal state for a fresh sequence
    setQuotes([]);
    setQuotesForAnimation([]);
    setAllowFinalTile(false);
    setHideTop3Permanently(false);
    setInitialLoadDone(false);
    setVisibleQuotes(0);
    scaledOnceRef.current.clear();
    setFireworksArmed(false);
    await fetchQuotes(next);
    setShowMobileFilterPanel(false);
  };

  const handleReset = () => {
    setFilterConfig((cfg) => {
      const inclusiveSelections = {};
      Object.entries(cfg).forEach(([key, def]) => {
        if (
          def?.type === 'checkbox' &&
          def.dynamicValues &&
          def.values?.length
        ) {
          inclusiveSelections[key] = def.values.map((v) => v.value);
        }
      });
      return { ...cfg, selectedSections: inclusiveSelections };
    });
    const allSchemes =
      filterConfig.schemename?.values?.map((v) => v.value) ?? null;
    setAppliedSchemes(allSchemes);
  };

  const filteredQuotes = quotes.filter((q) => {
    if (hideTop3Permanently && q.isTop3) return false;
    if (appliedSchemes !== null) {
      const schemeDef = FilterPanelData.schemename;
      const quotePath = schemeDef?.valuesPath ?? 'schemename';
      if (!appliedSchemes.includes(q[quotePath])) return false;
    }
    return true;
  });

  const [sortOption, setSortOption] = useState('priceLowHigh');

  const sortOptions = [
    { value: 'priceLowHigh', label: 'Price low - high' },
    { value: 'priceHighLow', label: 'Price high - low' },
  ];

  const sortedQuotes = [...filteredQuotes].sort((a, b) => {
    switch (sortOption) {
      case 'priceLowHigh':
        return (
          a.premium.monthlypremium_total - b.premium.monthlypremium_total ||
          a._rank - b._rank
        );
      case 'priceHighLow':
        return (
          b.premium.monthlypremium_total - a.premium.monthlypremium_total ||
          a._rank - b._rank
        );
      default:
        return 0;
    }
  });

  const handleModalButtonClick = (id) => {
    if (id === 'keep-searching') {
      setShowTeaseModal(false);
      setAllowFinalTile(true);
    } else if (id === 'stop-searching') {
      setShowTeaseModal(false);
      setHideTop3Permanently(true);
      setInitialLoadDone(true);
    }
  };

  const [showMobileFilterPanel, setShowMobileFilterPanel] = useState(false);

  const withheldCount = Math.min(3, quotes.length);
  const shownSoFar = Math.max(quotes.length - withheldCount, 0);

  const modalData = {
    title: `We found ${shownSoFar} so far`,
    content: 'But there might be an even better deal. Want us to keep looking?',
    type: 'found-deals',
    buttons: [
      {
        index: 0,
        id: 'keep-searching',
        type: 'primary-pink',
        text: 'Yes, find me the best deal',
      },
      {
        index: 1,
        id: 'stop-searching',
        type: 'secondary-gray',
        text: 'No, Stop Searching',
      },
    ],
  };

  const quotesToRender = initialLoadDone ? sortedQuotes : quotesForAnimation;

  const showNoMatches =
    !fetching && initialLoadDone && quotesToRender.length === 0;

  let summaryText = '';
  if (showNoMatches) {
    summaryText = 'No Quotes Match Your Criteria';
  } else if (initialLoadDone) {
    summaryText = `${quotesToRender.length} Deal${quotesToRender.length > 1 ? 's' : ''} — Avg. price R${Math.round(avgPremium)}`;
  } else if (quotesToRender.length > 0) {
    summaryText = `${quotesToRender.length} Deal${quotesToRender.length > 1 ? 's' : ''} Found So Far`;
  }

  const progress = useMemo(() => {
    const total = quotes.length;

    const MIN = 0;
    const PHASE1_MAX = 0.7;
    const PHASE2_MAX = 0.95;

    if (fetching) return MIN;
    if (total === 0) return 1;

    const withheld = Math.min(3, total);
    const nonTop3Target = Math.max(total - withheld, 1);

    const phase1Ratio = Math.min(visibleQuotes / nonTop3Target, 1);

    if (hideTop3Permanently) {
      const ratio = Math.min(visibleQuotes / nonTop3Target, 1);
      const scaled = MIN + (1 - MIN) * ratio;
      return Math.min(scaled, initialLoadDone ? 1 : PHASE2_MAX);
    }

    if (!allowFinalTile) {
      return Math.max(
        MIN,
        Math.min(MIN + (PHASE1_MAX - MIN) * phase1Ratio, PHASE1_MAX)
      );
    }

    const totalTargetVisible = total;
    const phase2Ratio = Math.min(
      visibleQuotes / Math.max(totalTargetVisible, 1),
      1
    );

    const scaledPhase2 = PHASE1_MAX + (PHASE2_MAX - PHASE1_MAX) * phase2Ratio;
    return initialLoadDone ? 1 : Math.min(scaledPhase2, PHASE2_MAX);
  }, [
    fetching,
    visibleQuotes,
    quotes,
    initialLoadDone,
    hideTop3Permanently,
    allowFinalTile,
  ]);

  useEffect(() => {
    if (showIntroPanel && introPanelRef.current) {
      const elements = introPanelRef.current.querySelectorAll('.stagger-in');
      if (elements.length > 0) {
        gsap.set(elements, { opacity: 0, y: 30 });
        setTimeout(() => {
          gsap.to(elements, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: 'power2.out',
            stagger: 0.1,
            overwrite: true,
          });
        }, 30);
      }
    }
  }, [showIntroPanel]);

  const HippoAvatar =
    'https://assets.askhippo.co.za/avatars/v2/Thumbs-up-v2.gif';

  return (
    <>
      {SeoTagsManager.render(pathname)}
      <Box
        sx={{
          width: '100%',
          px: { xs: 2, sm: 3 },
          mt: isMobile ? '0px' : '0.75rem',
          overflowX: 'clip',
        }}
      >
        {showIntroPanel && (
          <Box
            ref={introPanelRef}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap',
              mt: isTablet ? '24px' : isMobileOrTablet ? '0px' : '32px',
            }}
          >
            <img
              src={HippoAvatar}
              alt="Hippo Avatar"
              style={{
                width: '124px',
                height: '124px',
                objectFit: 'contain',
                margin: isTablet
                  ? '40px auto 0px'
                  : isMobile
                    ? '48px auto 0px'
                    : '0 auto 24px',
              }}
            />
            <Typography
              className="stagger-in"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: isMobileOrTablet ? '1.25rem' : '1.5rem',
                lineHeight: isMobileOrTablet ? '1.5rem' : '1.75rem',
                letterSpacing: '-0.03em',
                fontWeight: 400,
                color: '#162F3B',
                mb: isMobileOrTablet ? 2 : 4,
              }}
            >
              OK. We&rsquo;ve got what we need.
              <br />
              Ready to get your quotes?
            </Typography>

            <Button
              className="stagger-in"
              onClick={() => {
                setShowIntroPanel(false);
                if (!fetching) {
                  setTimeout(() => setIntroDelayDone(true), 2000);
                } else {
                  setIntroDelayDone(true);
                }
              }}
              sx={{
                backgroundColor: '#FF0074',
                color: '#fff',
                fontWeight: 700,
                fontSize: '1.25rem',
                lineHeight: '1.5rem',
                borderRadius: '18px',
                padding: isMobileOrTablet ? '12px 24px' : '12px 32px',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#e60073' },
              }}
            >
              Yes
            </Button>
          </Box>
        )}

        {!showIntroPanel && (
          <Box sx={{ width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
            <ResultHeaderComponent
              headerText={
                quotes.length === 0 ? 'Well, this is awkward...' : headerText
              }
              loadingText="Talking to medical schemes"
              initialLoadDone={initialLoadDone}
              sortOption={sortOption}
              setSortOption={setSortOption}
              progress={progress}
              sortOptions={sortOptions}
            />

            {introDelayDone && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  mt: '32px',
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      columnGap: '32px',
                      alignItems: 'flex-start',
                    }}
                  >
                    {/* Left Panel – Filter (desktop only) */}
                    {!isMobileOrTablet && (
                      <Box sx={{ flex: '0 0 12.1875rem' }}>
                        <FilterPanelComponent
                          config={filterConfig}
                          filterPanelHeader={formValues?.fullName}
                          filterPanelSubHeader={`${medicalAidAdults} Adult${
                            medicalAidAdults === 1 ? '' : 's'
                          }, ${medicalAidChildren} Child${
                            medicalAidChildren === 1 ? '' : 'ren'
                          }`}
                          setConfig={setFilterConfig}
                          onReset={handleReset}
                          onApply={handleApply}
                          onClose={() => setShowMobileFilterPanel(false)}
                          initialLoadDone={initialLoadDone}
                        />
                      </Box>
                    )}

                    {/* Mobile Filter Slide-Up */}
                    <AnimatePresence>
                      {isMobileOrTablet && showMobileFilterPanel && (
                        <>
                          <motion.div
                            onClick={() => setShowMobileFilterPanel(false)}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            style={{
                              position: 'fixed',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              backgroundColor: 'rgba(0, 0, 0, 0.4)',
                              backdropFilter: 'blur(4px)',
                              zIndex: 1400,
                            }}
                          />
                          <motion.div
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{ duration: 0.25, ease: 'easeOut' }}
                            style={{
                              position: 'fixed',
                              overflow: 'hidden',
                              top: '250px',
                              left: 0,
                              width: '100%',
                              height: 'calc(100% - 250px)',
                              backgroundColor: '#FFFFFF',
                              borderTopLeftRadius: '16px',
                              borderTopRightRadius: '16px',
                              boxShadow: '0 -2px 10px rgba(0,0,0,0.2)',
                              zIndex: 1500,
                              overflowY: 'auto',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <MobileFilterPanelComponent
                              config={filterConfig}
                              filterPanelHeader={formValues?.fullName}
                              filterPanelSubHeader={`${formValues?.adultsCoveredMedicalAid || 0} Adult${
                                formValues?.adultsCoveredMedicalAid === 1
                                  ? ''
                                  : 's'
                              }, ${formValues?.childrenCoveredMedicalAid || 0} Child${
                                formValues?.childrenCoveredMedicalAid === 1
                                  ? ''
                                  : 'ren'
                              }`}
                              setConfig={setFilterConfig}
                              onReset={handleReset}
                              onApply={handleApply}
                              onClose={() => setShowMobileFilterPanel(false)}
                              initialLoadDone={initialLoadDone}
                            />
                          </motion.div>
                        </>
                      )}
                    </AnimatePresence>

                    {/* Right Panel – Tiles */}
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        mb: isMobileOrTablet ? 'calc(48px + 24px)' : '32px',
                      }}
                    >
                      {fetching ? (
                        <Box
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: '#1E354E',
                            textAlign: 'center',
                            py: 4,
                          }}
                        />
                      ) : quotes.length === 0 ? (
                        <Box
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: '#1E354E',
                            textAlign: 'center',
                            py: 4,
                          }}
                        >
                          We searched all our partners, but couldn&rsquo;t find
                          a suitable offer for you right now.
                          <TakeMeHomeButton />
                        </Box>
                      ) : (
                        <>
                          <Box
                            sx={{
                              width: '100%',
                              fontFamily: 'Inter',
                              fontWeight: 600,
                              fontSize: '1rem',
                              lineHeight: '1.75rem',
                              color: '#1E354E',
                              px: isMobileOrTablet ? '16px' : '0',
                            }}
                          >
                            {summaryText}
                          </Box>
                          <AnimatePresence mode="popLayout" initial={false}>
                            {quotesToRender.map((quote) => {
                              const isBestDeal = quote.bestDeal;
                              const tileId = `tile-${quote.optionid}`;

                              return (
                                <motion.div
                                  data-tile-motion
                                  layout="position"
                                  key={quote.optionid}
                                  transition={{
                                    layout: {
                                      type: 'spring',
                                      stiffness: 520,
                                      damping: 42,
                                      mass: 0.9,
                                    },
                                    opacity: {
                                      duration: 0.28,
                                      ease: 'easeOut',
                                    },
                                    y: { duration: 0.28, ease: 'easeOut' },
                                  }}
                                  initial={{ opacity: 0, y: -14 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: 18 }}
                                  style={{
                                    position: 'relative',
                                    willChange: 'transform, opacity',
                                    zIndex: isBestDeal ? 2500 : 'auto',
                                  }}
                                  onAnimationStart={() => {
                                    if (!scaledOnceRef.current.has(tileId)) {
                                      const el =
                                        document.getElementById(tileId);
                                      if (el) {
                                        playEntrance(el, { isBestDeal });
                                        scaledOnceRef.current.add(tileId);
                                      }
                                    }
                                  }}
                                  onAnimationComplete={() => {
                                    setVisibleQuotes((prev) => prev + 1);
                                  }}
                                >
                                  <Box
                                    id={tileId}
                                    sx={{
                                      position: 'relative',
                                      overflow: 'visible',
                                      zIndex: isBestDeal ? 2500 : 1,
                                    }}
                                  >
                                    {isBestDeal && fireworksArmed && (
                                      <FireworksFromTop
                                        playSignal={fireworksSignal}
                                        isMobile={isMobileOrTablet}
                                      />
                                    )}

                                    <Box
                                      sx={{ position: 'relative', zIndex: 2 }}
                                    >
                                      <ProviderTile
                                        category={category}
                                        product={product}
                                        data={quote}
                                        initialLoadDone={
                                          isBestDeal || initialLoadDone
                                        }
                                        bestDealContentReady={
                                          isBestDeal
                                            ? bestDealContentReady
                                            : true
                                        }
                                        isMobile={isMobileOrTablet}
                                        onSelectQuote={handleSelectQuote}
                                        onBuyOnline={handleBuyOnline}
                                        ref={isBestDeal ? bestDealRef : null}
                                      />
                                    </Box>
                                  </Box>
                                </motion.div>
                              );
                            })}
                          </AnimatePresence>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Modal */}
            {showTeaseModal && modalData && (
              <PopupModalComponent
                open={showTeaseModal}
                onClose={() => {
                  setShowTeaseModal(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                data={modalData}
                scrollToTop
                onAction={handleModalButtonClick}
              />
            )}

            {/* Mobile Filter Button */}
            {isMobileOrTablet && (
              <Box
                onClick={() => setShowMobileFilterPanel(true)}
                sx={{
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  height: '48px',
                  backgroundColor: '#C7CA00',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: '24px',
                  zIndex: 9999,
                  cursor: 'pointer',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    lineHeight: '1.5rem',
                    letterSpacing: '-0.03em',
                    color: '#162F3B',
                  }}
                >
                  Filter
                </Box>
                <Box
                  component="span"
                  sx={{
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={FilterIcon}
                    alt="Unchecked"
                    style={{ width: '24px', height: '24px', display: 'block' }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </>
  );
}

/** Wrapper to inject mobile flags */
const ResultPageWithMobile = (props) => {
  const { isMobile, isTablet } = useMobileContext();
  return <ResultPage {...props} isMobile={isMobile} isTablet={isTablet} />;
};

export default withRouter(ResultPageWithMobile);
